import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { API_BASE_URL as API_BASE } from '../../config/api';
import './AdminMembershipFormPage.css';

const AdminMembershipFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subTitle: '',
    // Monthly Plan
    monthlyPrice: '',
    monthlyDuration: 'MO/YR',
    monthlyPriceAlt: '',
    // Annual Plan
    annualPrice: '',
    annualDuration: 'MO/YR',
    // Monthly OR
    monthlyOrPrice: '',
    monthlyOrDuration: 'MO/YR',
    monthlyOrPriceAlt: '',
    savingsAmount: '',
    // Features
    productPostingLimit: '200',
    productPostingEnabled: false,
    serviceKeyLinksEnabled: false,
    serviceKeyLinksValue: 'Allowed',
    viewBuyerContactEnabled: false,
    viewBuyerContactValue: 'Unlimited',
    receiveDirectCallsEnabled: false,
    buyerDirectoryAccessEnabled: false,
    buyerDirectoryAccessValue: 'Unlimited',
    uploadVideosEnabled: false,
    maxProductImages: '5',
    maxProductImagesEnabled: false,
    companyProfileWithLogoEnabled: false,
    liveChatWithBuyersEnabled: false,
    highlightBrandEnabled: false,
    supplierDashboardEnabled: false,
    addProfileToMarketplaceEnabled: false,
    submitProjectsEnabled: false,
    postNewsEnabled: false,
    trustedBadgeEnabled: false,
    trustedBadgeValue: 'Available with Extra Fee',
    isActive: true,
    sortOrder: 0
  });

  useEffect(() => {
    if (isEditing) {
      fetchPlan();
    }
  }, [id]);

  const fetchPlan = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/membership/admin/plans/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const plan = await response.json();
        setFormData(prev => ({
          ...prev,
          title: plan.displayName || plan.title || '',
          subTitle: plan.description || plan.subTitle || '',
          monthlyPrice: plan.monthlyPrice?.toString() || '',
          annualPrice: plan.annualPrice?.toString() || '',
          productPostingLimit: plan.productPostingLimit?.toString() || '200',
          productPostingEnabled: plan.productPostingLimit > 0,
          serviceKeyLinksEnabled: plan.canReceiveRFQs ?? false,
          viewBuyerContactEnabled: plan.canViewBuyerContacts ?? false,
          receiveDirectCallsEnabled: plan.canReceiveDirectCalls ?? false,
          buyerDirectoryAccessEnabled: plan.buyerDirectoryAccess ?? false,
          uploadVideosEnabled: plan.canUploadVideos ?? false,
          maxProductImages: plan.maxProductImages?.toString() || '5',
          maxProductImagesEnabled: plan.maxProductImages > 0,
          companyProfileWithLogoEnabled: plan.hasCompanyProfile ?? false,
          liveChatWithBuyersEnabled: plan.canLiveChat ?? false,
          highlightBrandEnabled: plan.canHighlightBrand ?? false,
          supplierDashboardEnabled: plan.hasDashboardAccess ?? false,
          addProfileToMarketplaceEnabled: plan.canAddProfessional ?? false,
          submitProjectsEnabled: plan.canSubmitProjects ?? false,
          postNewsEnabled: plan.canPostNews ?? false,
          trustedBadgeEnabled: plan.trustedBadgeAvailable ?? false,
          isActive: plan.isActive ?? true,
          sortOrder: plan.sortOrder || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleToggleChange = (fieldName) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Map form data to API format
      const apiData = {
        name: formData.title.toLowerCase().replace(/\s+/g, '-'),
        displayName: formData.title,
        description: formData.subTitle,
        monthlyPrice: parseFloat(formData.monthlyPrice) || 0,
        annualPrice: parseFloat(formData.annualPrice) || 0,
        productPostingLimit: parseInt(formData.productPostingLimit) || 0,
        canReceiveRFQs: formData.serviceKeyLinksEnabled,
        rfqRegion: 'global',
        canViewBuyerContacts: formData.viewBuyerContactEnabled,
        buyerContactRegion: formData.viewBuyerContactValue === 'Unlimited' ? 'all' : 'gcc',
        canReceiveDirectCalls: formData.receiveDirectCallsEnabled,
        buyerDirectoryAccess: formData.buyerDirectoryAccessEnabled,
        buyerDirectoryRegion: formData.buyerDirectoryAccessValue === 'Unlimited' ? 'all' : 'gcc',
        canUploadVideos: formData.uploadVideosEnabled,
        maxProductImages: parseInt(formData.maxProductImages) || 5,
        hasCompanyProfile: formData.companyProfileWithLogoEnabled,
        canLiveChat: formData.liveChatWithBuyersEnabled,
        canHighlightBrand: formData.highlightBrandEnabled,
        hasDashboardAccess: formData.supplierDashboardEnabled,
        canAddProfessional: formData.addProfileToMarketplaceEnabled,
        canSubmitProjects: formData.submitProjectsEnabled,
        canPostNews: formData.postNewsEnabled,
        trustedBadgeAvailable: formData.trustedBadgeEnabled,
        isActive: formData.isActive,
        sortOrder: formData.sortOrder
      };

      const url = isEditing
        ? `${API_BASE}/membership/admin/plans/${id}`
        : `${API_BASE}/membership/admin/plans`;

      const method = isEditing ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');

      const response = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(apiData)
      });

      if (response.ok) {
        alert(isEditing ? 'Plan updated successfully!' : 'Plan created successfully!');
        navigate('/admin/membership');
      } else {
        alert('Failed to save plan');
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Error saving plan');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/membership');
  };

  // Toggle Switch Component
  const ToggleSwitch = ({ checked, onChange, disabled }) => (
    <label className="mf-toggle-switch">
      <input 
        type="checkbox" 
        checked={checked} 
        onChange={onChange}
        disabled={disabled}
      />
      <span className="mf-toggle-slider"></span>
    </label>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="membership-form-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="membership-form-container">
        {/* Header */}
        <div className="mf-header">
          <div className="mf-header-left">
            <h1 className="mf-title">Membership Packages</h1>
            <p className="mf-subtitle">Flexible plans designed for suppliers, buyers, and professionals at every level.</p>
          </div>
        </div>

        {/* Form Card */}
        <div className="mf-form-card">
          <form onSubmit={handleSubmit}>
            {/* Title & Sub Title */}
            <div className="mf-section">
              <div className="mf-form-row-single">
                <div className="mf-form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Annual Industrian"
                  />
                </div>
              </div>
              <div className="mf-form-row-single">
                <div className="mf-form-group">
                  <label>Sub Title</label>
                  <input
                    type="text"
                    name="subTitle"
                    value={formData.subTitle}
                    onChange={handleInputChange}
                    placeholder=""
                  />
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="mf-pricing-section">
              {/* Monthly Plan Row */}
              <div className="mf-pricing-row">
                <div className="mf-pricing-label">Monthly Plan</div>
                <div className="mf-pricing-inputs">
                  <input
                    type="text"
                    name="monthlyPrice"
                    value={formData.monthlyPrice}
                    onChange={handleInputChange}
                    placeholder="Price"
                    className="mf-price-input"
                  />
                  <select
                    name="monthlyDuration"
                    value={formData.monthlyDuration}
                    onChange={handleInputChange}
                    className="mf-duration-select"
                  >
                    <option value="MO/YR">MO/YR</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                  <input
                    type="text"
                    name="monthlyPriceAlt"
                    value={formData.monthlyPriceAlt}
                    onChange={handleInputChange}
                    placeholder="Price"
                    className="mf-price-input"
                  />
                </div>
                <div className="mf-pricing-right">
                  <span className="mf-pricing-right-label">Annual Plan</span>
                  <div className="mf-pricing-right-inputs">
                    <input
                      type="text"
                      name="annualPrice"
                      value={formData.annualPrice}
                      onChange={handleInputChange}
                      placeholder="Price"
                      className="mf-price-input"
                    />
                    <select
                      name="annualDuration"
                      value={formData.annualDuration}
                      onChange={handleInputChange}
                      className="mf-duration-select"
                    >
                      <option value="MO/YR">MO/YR</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Yearly">Yearly</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Monthly OR Row */}
              <div className="mf-pricing-row">
                <div className="mf-pricing-label">Monthly OR</div>
                <div className="mf-pricing-inputs">
                  <input
                    type="text"
                    name="monthlyOrPrice"
                    value={formData.monthlyOrPrice}
                    onChange={handleInputChange}
                    placeholder="Price"
                    className="mf-price-input"
                  />
                  <select
                    name="monthlyOrDuration"
                    value={formData.monthlyOrDuration}
                    onChange={handleInputChange}
                    className="mf-duration-select"
                  >
                    <option value="MO/YR">MO/YR</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                  <input
                    type="text"
                    name="monthlyOrPriceAlt"
                    value={formData.monthlyOrPriceAlt}
                    onChange={handleInputChange}
                    placeholder="Price"
                    className="mf-price-input"
                  />
                </div>
                <div className="mf-pricing-right">
                  <span className="mf-pricing-right-label">Save</span>
                  <div className="mf-pricing-right-inputs mf-save-indicator">
                    <span className="mf-save-text">0 m...</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="mf-features-section">
              {/* Product / Service Posting limit */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="productPostingEnabled"
                    checked={formData.productPostingEnabled}
                    onChange={() => handleToggleChange('productPostingEnabled')}
                  />
                  <label htmlFor="productPostingEnabled">Product / Service Posting limit</label>
                </div>
                <div className="mf-feature-value">
                  <input
                    type="text"
                    name="productPostingLimit"
                    value={formData.productPostingLimit}
                    onChange={handleInputChange}
                    className="mf-feature-input"
                  />
                </div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.productPostingEnabled}
                    onChange={() => handleToggleChange('productPostingEnabled')}
                  />
                </div>
              </div>

              {/* Service key Links / API's */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="serviceKeyLinksEnabled"
                    checked={formData.serviceKeyLinksEnabled}
                    onChange={() => handleToggleChange('serviceKeyLinksEnabled')}
                  />
                  <label htmlFor="serviceKeyLinksEnabled">Service key Links / API's</label>
                </div>
                <div className="mf-feature-value">
                  <select
                    name="serviceKeyLinksValue"
                    value={formData.serviceKeyLinksValue}
                    onChange={handleInputChange}
                    className="mf-feature-select"
                  >
                    <option value="Allowed">Allowed</option>
                    <option value="Not Allowed">Not Allowed</option>
                  </select>
                </div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.serviceKeyLinksEnabled}
                    onChange={() => handleToggleChange('serviceKeyLinksEnabled')}
                  />
                </div>
              </div>

              {/* View Buyer Contact Details */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="viewBuyerContactEnabled"
                    checked={formData.viewBuyerContactEnabled}
                    onChange={() => handleToggleChange('viewBuyerContactEnabled')}
                  />
                  <label htmlFor="viewBuyerContactEnabled">View Buyer Contact Details</label>
                </div>
                <div className="mf-feature-value">
                  <select
                    name="viewBuyerContactValue"
                    value={formData.viewBuyerContactValue}
                    onChange={handleInputChange}
                    className="mf-feature-select"
                  >
                    <option value="Unlimited">Unlimited</option>
                    <option value="10/buyers">10/buyers</option>
                    <option value="50/buyers">50/buyers</option>
                  </select>
                </div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.viewBuyerContactEnabled}
                    onChange={() => handleToggleChange('viewBuyerContactEnabled')}
                  />
                </div>
              </div>

              {/* Receive Direct Calls from Buyers */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="receiveDirectCallsEnabled"
                    checked={formData.receiveDirectCallsEnabled}
                    onChange={() => handleToggleChange('receiveDirectCallsEnabled')}
                  />
                  <label htmlFor="receiveDirectCallsEnabled">Receive Direct Calls from Buyers</label>
                </div>
                <div className="mf-feature-value"></div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.receiveDirectCallsEnabled}
                    onChange={() => handleToggleChange('receiveDirectCallsEnabled')}
                  />
                </div>
              </div>

              {/* Buyer Directory Access */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="buyerDirectoryAccessEnabled"
                    checked={formData.buyerDirectoryAccessEnabled}
                    onChange={() => handleToggleChange('buyerDirectoryAccessEnabled')}
                  />
                  <label htmlFor="buyerDirectoryAccessEnabled">Buyer Directory Access</label>
                </div>
                <div className="mf-feature-value">
                  <select
                    name="buyerDirectoryAccessValue"
                    value={formData.buyerDirectoryAccessValue}
                    onChange={handleInputChange}
                    className="mf-feature-select"
                  >
                    <option value="Unlimited">Unlimited</option>
                    <option value="Limited">Limited</option>
                  </select>
                </div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.buyerDirectoryAccessEnabled}
                    onChange={() => handleToggleChange('buyerDirectoryAccessEnabled')}
                  />
                </div>
              </div>

              {/* Upload Videos */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="uploadVideosEnabled"
                    checked={formData.uploadVideosEnabled}
                    onChange={() => handleToggleChange('uploadVideosEnabled')}
                  />
                  <label htmlFor="uploadVideosEnabled">Upload Videos</label>
                </div>
                <div className="mf-feature-value"></div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.uploadVideosEnabled}
                    onChange={() => handleToggleChange('uploadVideosEnabled')}
                  />
                </div>
              </div>

              {/* Max Product Images per listing */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="maxProductImagesEnabled"
                    checked={formData.maxProductImagesEnabled}
                    onChange={() => handleToggleChange('maxProductImagesEnabled')}
                  />
                  <label htmlFor="maxProductImagesEnabled">Max Product Images per listing</label>
                </div>
                <div className="mf-feature-value">
                  <input
                    type="text"
                    name="maxProductImages"
                    value={formData.maxProductImages}
                    onChange={handleInputChange}
                    className="mf-feature-input"
                  />
                </div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.maxProductImagesEnabled}
                    onChange={() => handleToggleChange('maxProductImagesEnabled')}
                  />
                </div>
              </div>

              {/* Company Profile with Logo */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="companyProfileWithLogoEnabled"
                    checked={formData.companyProfileWithLogoEnabled}
                    onChange={() => handleToggleChange('companyProfileWithLogoEnabled')}
                  />
                  <label htmlFor="companyProfileWithLogoEnabled">Company Profile with Logo</label>
                </div>
                <div className="mf-feature-value"></div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.companyProfileWithLogoEnabled}
                    onChange={() => handleToggleChange('companyProfileWithLogoEnabled')}
                  />
                </div>
              </div>

              {/* Live Chat with Buyers */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="liveChatWithBuyersEnabled"
                    checked={formData.liveChatWithBuyersEnabled}
                    onChange={() => handleToggleChange('liveChatWithBuyersEnabled')}
                  />
                  <label htmlFor="liveChatWithBuyersEnabled">Live Chat with Buyers</label>
                </div>
                <div className="mf-feature-value"></div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.liveChatWithBuyersEnabled}
                    onChange={() => handleToggleChange('liveChatWithBuyersEnabled')}
                  />
                </div>
              </div>

              {/* Highlight Your Brand (Spotlight) */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="highlightBrandEnabled"
                    checked={formData.highlightBrandEnabled}
                    onChange={() => handleToggleChange('highlightBrandEnabled')}
                  />
                  <label htmlFor="highlightBrandEnabled">Highlight Your Brand (Spotlight)</label>
                </div>
                <div className="mf-feature-value"></div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.highlightBrandEnabled}
                    onChange={() => handleToggleChange('highlightBrandEnabled')}
                  />
                </div>
              </div>

              {/* Supplier Dashboard Access */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="supplierDashboardEnabled"
                    checked={formData.supplierDashboardEnabled}
                    onChange={() => handleToggleChange('supplierDashboardEnabled')}
                  />
                  <label htmlFor="supplierDashboardEnabled">Supplier Dashboard Access</label>
                </div>
                <div className="mf-feature-value"></div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.supplierDashboardEnabled}
                    onChange={() => handleToggleChange('supplierDashboardEnabled')}
                  />
                </div>
              </div>

              {/* Add Profile to Marketplace Section */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="addProfileToMarketplaceEnabled"
                    checked={formData.addProfileToMarketplaceEnabled}
                    onChange={() => handleToggleChange('addProfileToMarketplaceEnabled')}
                  />
                  <label htmlFor="addProfileToMarketplaceEnabled">Add Profile to Marketplace Section</label>
                </div>
                <div className="mf-feature-value"></div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.addProfileToMarketplaceEnabled}
                    onChange={() => handleToggleChange('addProfileToMarketplaceEnabled')}
                  />
                </div>
              </div>

              {/* Submit Proposal in Projects Section */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="submitProjectsEnabled"
                    checked={formData.submitProjectsEnabled}
                    onChange={() => handleToggleChange('submitProjectsEnabled')}
                  />
                  <label htmlFor="submitProjectsEnabled">Submit Proposal in Projects Section</label>
                </div>
                <div className="mf-feature-value"></div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.submitProjectsEnabled}
                    onChange={() => handleToggleChange('submitProjectsEnabled')}
                  />
                </div>
              </div>

              {/* Post News in News Section by Pre-Admin */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="postNewsEnabled"
                    checked={formData.postNewsEnabled}
                    onChange={() => handleToggleChange('postNewsEnabled')}
                  />
                  <label htmlFor="postNewsEnabled">Post News in News Section by Pre-Admin</label>
                </div>
                <div className="mf-feature-value"></div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.postNewsEnabled}
                    onChange={() => handleToggleChange('postNewsEnabled')}
                  />
                </div>
              </div>

              {/* Trusted Supplier Badge (Add-on) */}
              <div className="mf-feature-row">
                <div className="mf-feature-checkbox">
                  <input
                    type="checkbox"
                    id="trustedBadgeEnabled"
                    checked={formData.trustedBadgeEnabled}
                    onChange={() => handleToggleChange('trustedBadgeEnabled')}
                  />
                  <label htmlFor="trustedBadgeEnabled">Trusted Supplier Badge ( Add-on)</label>
                </div>
                <div className="mf-feature-value">
                  <span className="mf-badge-text">{formData.trustedBadgeValue}</span>
                </div>
                <div className="mf-feature-toggle">
                  <ToggleSwitch
                    checked={formData.trustedBadgeEnabled}
                    onChange={() => handleToggleChange('trustedBadgeEnabled')}
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="mf-form-actions">
              <button type="button" className="mf-btn-cancel" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="mf-btn-save" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMembershipFormPage;
